import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["filterButton", "templateContainer", "templateWrapper"];

  connect() {
    this.locale = window.location.pathname.split("/")[1].toUpperCase();
    let called = false;

    const onScroll = () => {
      if (!called) {
        called = true;
        this.fetchTemplates();
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll);
  }

  fetchTemplates() {
    fetch('/index-get-templates', { method: 'GET' })
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok: ' + res.status);
        return res.text();
      })
      .then(html => {
        this.templateWrapperTarget.innerHTML = html;
        this.loadTemplates();
        this.initializeFilter();
        this.setActiveFilter("pdf");
      })
      .catch(err => console.error('Fetch error:', err));
  }

  initializeFilter() {
    this.filterButtonTargets.forEach(button => {
      button.addEventListener("click", () => {
        const filterValue = button.getAttribute("data-initial-value").toLowerCase();
        this.setActiveFilter(filterValue);
      });
    });
  }

  setActiveFilter(filterValue) {
    this.activeFilter = filterValue;
    this.filterButtonTargets.forEach(button => {
      button.classList.toggle(
        "active",
        button.getAttribute("data-initial-value").toLowerCase() === filterValue
      );
    });
    this.loadTemplates();
  }

  loadTemplates() {
    const templatesData = JSON.parse(this.templateContainerTarget.dataset.templates);

    const filteredTemplates = templatesData
      .filter(template => template.title.toLowerCase() === this.activeFilter)
      .flatMap(template =>
        template.pagesSortedByPositionMeQr.map(page => ({
          id: page.id,
          image: page.image,
          type: page.type,
          title: template.title,
        }))
      );

    this.generateCarousel(filteredTemplates);
  }

  getChunkSize() {
    if (window.innerWidth < 768) return 2;
    if (window.innerWidth < 992) return 4;
    return 6;
  }

  groupByWidth(templates, chunkSize) {
    const slides = [];
    let currentSlide = [];
    let currentWidth = 0;

    templates.forEach(tpl => {
      const tplWidth = tpl.type === 1 ? 2 : 1;

      if (currentWidth + tplWidth > chunkSize) {
        slides.push(currentSlide);
        currentSlide = [];
        currentWidth = 0;
      }

      currentSlide.push(tpl);
      currentWidth += tplWidth;
    });

    if (currentSlide.length) slides.push(currentSlide);


    if (!slides.length || slides.length === 1) return slides;

    let lastSlide = slides[slides.length - 1];
    const getWidth = slide => slide.reduce((sum, t) => sum + (t.type === 1 ? 2 : 1), 0);

    while (getWidth(lastSlide) < chunkSize) {
      let filled = false;
      for (let i = slides.length - 2; i >= 0; i--) {
        const prevSlide = slides[i];
        for (let j = prevSlide.length - 1; j >= 0; j--) {
          const tpl = prevSlide[j];
          const tplWidth = tpl.type === 1 ? 2 : 1;
          if (getWidth(lastSlide) + tplWidth <= chunkSize) {
            lastSlide.unshift(tpl);
            filled = true;
          }
          if (getWidth(lastSlide) >= chunkSize) break;
        }
        if (getWidth(lastSlide) >= chunkSize) break;
      }
      if (!filled) break;
    }

    return slides;
  }


  generateCarousel(templates) {
    const chunkSize = this.getChunkSize();
    const templateChunks = this.groupByWidth(templates, chunkSize);

    const slidesHTML = templateChunks
      .map((chunk, index) => {
        const templatesHTML = chunk
          .map(page => `
            <div class="${
            page.type === 1
              ? "col-lg-4 col-md-6 col-12"
              : "col-lg-2 col-md-3 col-6"
          } text-center">
              <a href="/entry/constructor/clone/${page.id}"
                 class="d-block template-main-item-img overflow-hidden template-link"
                 data-title="${page.title}">
                <img src="${page.image}" alt="Template Image ${page.id}" class="img-fluid">
              </a>
            </div>
          `)
          .join("");

        return `
          <div class="carousel-item ${index === 0 ? "active" : ""}">
            <div class="row gx-3 justify-content-xl-center justify-content-between mt-4 mb-4">
              ${templatesHTML}
            </div>
          </div>
        `;
      })
      .join("");

    const indicatorsHTML = templateChunks.length > 1
      ? templateChunks
        .map(
          (_, index) => `
          <button type="button" data-bs-target="#carouselExampleCaptions"
            data-bs-slide-to="${index}"
            class="${index === 0 ? "active" : ""}"
            aria-current="${index === 0 ? "true" : "false"}"
            aria-label="Slide ${index + 1}">
          </button>
        `
        )
        .join("")
      : "";

    this.templateContainerTarget.innerHTML = `
      <div id="carouselExampleCaptions" class="carousel slide" data-bs-ride="carousel" data-bs-interval="3000">
        ${indicatorsHTML ? `<div class="carousel-indicators">${indicatorsHTML}</div>` : ""}
        <div class="carousel-inner">
          ${slidesHTML}
        </div>
      </div>
    `;

    const carouselElement = document.querySelector("#carouselExampleCaptions");
    if (carouselElement) {
      new bootstrap.Carousel(carouselElement, { interval: 3000, ride: "carousel" });
    }

    const links = this.templateContainerTarget.querySelectorAll(".template-link");
    links.forEach(link => {
      link.addEventListener("click", () => {
        gtag("event", "select_qr_template", {
          site_language: this.locale.length > 2 || !this.locale ? "EN" : this.locale,
          type: link.dataset.title,
        });

        if (typeof rdt === "function") {
          rdt("track", "Custom", { customEventName: "select_qr_template" });
        }
      });
    });
  }
}
